import React, { useEffect, useState, useCallback, useRef } from "react";
import useInfiniteScroll from 'react-infinite-scroll-hook';

import { fetchNewestPatternsFull, searchPatternsWithFilters } from '../../../api/fetchers';
import { useAuth } from "../../../context/AuthContext";
import type { PatternPublicResponse } from '@mrb91002/shared-types';

import { HomeBanner, PatternCards } from "./components";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "most-reviewed", label: "Most Reviewed" },
  { value: "highest-rated", label: "Highest Rated" },
] as const;

const DATE_RANGE_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
] as const;

export const Home = () => {
  const { isLoading, session, idToken } = useAuth();
  const [patterns, setPatterns] = useState<PatternPublicResponse[]>([]);
  const [lastKey, setLastKey] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0].value);
  const [dateRange, setDateRange] = useState(DATE_RANGE_OPTIONS[0].value);
  const [hasMore, setHasMore] = useState(true);
  const [from, setFrom] = useState(0);
  const [total, setTotal] = useState(0);
  
  // Page load logging - runs once on mount
  useEffect(() => {
    console.log('📄 [PAGE] Home page loaded');
  }, []);

  useEffect(() => {
    setPatterns([]);
    setLastKey(null);
    setFrom(0);
    setFetchError(null);
    setHasMore(true);
    loadPatterns(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, dateRange]);

  const loadPatterns = useCallback(
    async (reset: boolean = false): Promise<void> => {
      try {
        if (reset) {
          setLoading(true);
        } else {
          setIsLoadingMore(true);
        }
        setFetchError(null);
        
        // Use OpenSearch for sorting/filtering, DynamoDB for newest only
        if (sortBy === 'newest' && dateRange === 'all') {
          const keyToUse = reset ? null : lastKey;
          const result = await fetchNewestPatternsFull(50, keyToUse);
          const newItems = result.items || [];
          const nextKey = result.lastKey || null;
          
          if (reset) {
            setPatterns(newItems);
          } else {
            setPatterns(prev => [...prev, ...newItems]);
          }
          setLastKey(nextKey);
          setHasMore(!!nextKey);
        } else {
          const fromValue = reset ? 0 : from;
          const result = await searchPatternsWithFilters(sortBy as any, dateRange as any, 50, fromValue);
          const newItems = result.items || [];
          
          if (reset) {
            setPatterns(newItems);
            setFrom(newItems.length);
          } else {
            setPatterns(prev => [...prev, ...newItems]);
            setFrom(prev => prev + newItems.length);
          }
          setTotal(result.total);
          setHasMore(fromValue + newItems.length < result.total);
        }
      } catch (error: unknown) {
        setFetchError(error instanceof Error ? error.message : 'Error fetching display data');
        console.error('Error fetching display data:', error);
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [lastKey, sortBy, dateRange, from]
  );

  const [infiniteRef] = useInfiniteScroll({
    loading: isLoadingMore,
    hasNextPage: hasMore,
    onLoadMore: () => loadPatterns(),
    disabled: !!fetchError,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value);
  };
  
  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDateRange(e.target.value);
  };

  if (isLoading && patterns.length === 0) return <p>Loading...</p>;

  console.log('[HOME] session', session);
  console.log('[HOME] idToken', idToken);
  console.log('[HOME] hostname:', window.location.hostname);
  console.log('[HOME] localStorage fpw_beta:', localStorage.getItem('fpw_beta'));
  console.log('[HOME] localStorage fpw_invite_code:', localStorage.getItem('fpw_invite_code'));

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh", minWidth: 0, padding: 0, margin: 0 }}>
      <HomeBanner message/>

      <main style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: "30px 18px 0 18px", // add top padding matching the modal's overlap (90px)
        position: "relative"
      }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            margin: "40px 0 22px 0"
          }}
        >
          <h2 style={{
            fontWeight: 700,
            fontSize: "1.56rem",
            margin: 0,
            letterSpacing: "-0.01em",
            color: "var(--navy-700)"
          }}>
            Newest Patterns
          </h2>
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label htmlFor="sort-select" style={{ fontSize: 16, color: "var(--ink)" }}>Sort:</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={handleSortChange}
                style={{
                  borderRadius: 7,
                  padding: "6px 16px",
                  fontSize: 16,
                  border: "1px solid var(--border)",
                  background: "#fff",
                  fontWeight: 500,
                  color: "var(--navy-600)"
                }}
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {sortBy !== 'newest' && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label htmlFor="date-range-select" style={{ fontSize: 16, color: "var(--ink)" }}>From:</label>
                <select
                  id="date-range-select"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  style={{
                    borderRadius: 7,
                    padding: "6px 16px",
                    fontSize: 16,
                    border: "1px solid var(--border)",
                    background: "#fff",
                    fontWeight: 500,
                    color: "var(--navy-600)"
                  }}
                >
                  {DATE_RANGE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          {patterns.length ? (
            <>
              <PatternCards patterns={patterns} />
              
              {/* Loading more indicator */}
              {isLoadingMore && (
                <div style={{ textAlign: "center", padding: "32px", color: "#64748b", fontSize: 16 }}>
                  <div style={{
                    width: 24,
                    height: 24,
                    border: "2px solid #e2e8f0",
                    borderTop: "2px solid #3b82f6",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    display: "inline-block",
                    marginRight: 12
                  }} />
                  Loading more patterns...
                </div>
              )}
              
              {/* Infinite scroll trigger */}
              {hasMore && <div ref={infiniteRef} />}
              

              
              {/* End of results indicator */}
              {!hasMore && patterns.length > 0 && (
                <div style={{
                  textAlign: "center",
                  padding: "32px",
                  color: "#64748b",
                  fontSize: 16,
                  borderTop: "1px solid #e2e8f0",
                  marginTop: 24,
                  background: "#f8fafc",
                  borderRadius: 8
                }}>
                  You've reached the end of the patterns
                </div>
              )}
            </>
          ) : fetchError ? (
            <p style={{ color: 'var(--danger)', textAlign: 'center' }}>Error: {fetchError}</p>
          ) : (
            <p style={{ textAlign: 'center' }}>Loading patterns...</p>
          )}
        </div>
      </main>

    </div>
  );
};

export default Home;
